import { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Alert, CircularProgress, Box, Divider, Typography,
    FormControl, InputLabel, Select, MenuItem, TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

import Api from '../../Services/Api';

function CustomerPdfForm({ customerId, open, onClose, onSuccess }) {
    const [templates, setTemplates] = useState([]);
    const [installations, setInstallations] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [selectedInstallation, setSelectedInstallation] = useState('');
    const [recipientPhone, setRecipientPhone] = useState('');
    const [phoneNumbers, setPhoneNumbers] = useState([]);
    const [placeholderValues, setPlaceholderValues] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return;
        let ignore = false;
        setError(null);
        setSelectedTemplate('');
        setSelectedInstallation('');
        setRecipientPhone('');
        setPhoneNumbers([]);
        setPlaceholderValues({});

        Promise.all([
            Api.get({ endpoint: 'pdf-template', contentType: 'application/json' }),
            Api.get({ endpoint: `installation/list/${customerId}`, contentType: 'application/json' }),
            Api.get({ endpoint: `customer/${customerId}`, contentType: 'application/json' }),
        ])
            .then(([tplRes, instRes, customerRes]) => {
                if (ignore) return;
                setTemplates(tplRes.data.templates ?? []);
                setInstallations(instRes.data.installations ?? []);
                const c = customerRes.data.customer ?? {};
                const phones = [
                    c.phone_number_1 ? { number: c.phone_number_1, ref: [c.ref_1, c.ref_type_1].filter(Boolean).join(' ') } : null,
                    c.phone_number_2 ? { number: c.phone_number_2, ref: [c.ref_2, c.ref_type_2].filter(Boolean).join(' ') } : null,
                    c.phone_number_3 ? { number: c.phone_number_3, ref: [c.ref_3, c.ref_type_3].filter(Boolean).join(' ') } : null,
                ].filter(Boolean);
                setPhoneNumbers(phones);
                setRecipientPhone(phones[0]?.number ?? '');
            })
            .catch(() => { if (!ignore) setError('Errore nel caricamento dei dati. Riprovare.'); });
        return () => { ignore = true; };
    }, [open, customerId]); // eslint-disable-line react-hooks/exhaustive-deps

    const currentTemplate = templates.find(t => t.id === selectedTemplate) ?? null;

    // placeholders: {"Label": {"key": "...", "type": "text|date|currency|integer"}}
    const parsePlaceholders = (raw) => {
        if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return [];
        return Object.entries(raw).map(([label, val]) => ({
            label,
            key:  typeof val === 'object' ? val.key  : val,
            type: typeof val === 'object' ? (val.type ?? 'text') : 'text',
        }));
    };

    const handleTemplateChange = (e) => {
        const id = e.target.value;
        setSelectedTemplate(id);
        const tpl = templates.find(t => t.id === id);
        const initialValues = {};
        parsePlaceholders(tpl?.placeholders).forEach(({ key, type }) => {
            initialValues[key] = type === 'date' ? null : '';
        });
        setPlaceholderValues(initialValues);
    };

    const handlePlaceholderChange = (key, value) => {
        setPlaceholderValues(prev => ({ ...prev, [key]: value }));
    };

    const handleSubmit = () => {
        if (!selectedTemplate) {
            setError('Selezionare un template.');
            return;
        }
        if (recipientPhone && !/^3\d{8,9}$/.test(recipientPhone)) {
            setError('Il numero di cellulare non è valido (deve iniziare con 3 e avere 9 o 10 cifre).');
            return;
        }
        setLoading(true);
        setError(null);

        // Formatta i valori prima di inviarli: le date diventano DD/MM/YYYY
        const formattedValues = {};
        parsePlaceholders(currentTemplate?.placeholders).forEach(({ key, type }) => {
            const val = placeholderValues[key];
            if (type === 'date') {
                formattedValues[key] = val && dayjs(val).isValid() ? dayjs(val).format('DD/MM/YYYY') : '';
            } else {
                formattedValues[key] = val != null ? String(val) : '';
            }
        });

        Api.post({
            endpoint: 'document/generate',
            contentType: 'application/json',
            body: {
                template_id:     selectedTemplate,
                customer_id:     parseInt(customerId),
                installation_id: selectedInstallation || null,
                recipient_phone: recipientPhone || null,
                text_values:     formattedValues,
            },
        })
            .then(response => {
                const sentPdfId = response.data.sent_pdf.id;
                return Api.get({ endpoint: `document/download-docx/${sentPdfId}`, responseType: 'blob' })
                    .then(blobRes => ({ sentPdfId, blobRes, sentPdf: response.data.sent_pdf }));
            })
            .then(({ sentPdfId, blobRes, sentPdf }) => {
                const disposition = blobRes.headers['content-disposition'] ?? '';
                const match = disposition.match(/filename[^;=\n]*=['"]?([^'"\n;]+)['"]?/i);
                const fileName = match ? match[1] : `${currentTemplate?.title ?? 'documento'}_${sentPdfId}.docx`;
                const url = window.URL.createObjectURL(new Blob([blobRes.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                onSuccess(sentPdf);
            })
            .catch(() => setError('Errore durante la generazione del documento. Riprovare.'))
            .finally(() => setLoading(false));
    };

    const renderPlaceholderField = ({ label, key, type }) => {
        if (type === 'date') {
            return (
                <DatePicker
                    key={key}
                    label={label}
                    value={placeholderValues[key] ?? null}
                    onChange={val => handlePlaceholderChange(key, val)}
                    format="DD/MM/YYYY"
                    slotProps={{ textField: { fullWidth: true } }}
                />
            );
        }
        if (type === 'currency') {
            return (
                <TextField
                    key={key}
                    label={label}
                    value={placeholderValues[key] ?? ''}
                    onChange={e => handlePlaceholderChange(key, e.target.value)}
                    fullWidth
                    type="number"
                    inputProps={{ step: '0.01', min: '0' }}
                />
            );
        }
        if (type === 'integer') {
            return (
                <TextField
                    key={key}
                    label={label}
                    value={placeholderValues[key] ?? ''}
                    onChange={e => handlePlaceholderChange(key, e.target.value)}
                    fullWidth
                    type="number"
                    inputProps={{ step: '1', min: '0' }}
                />
            );
        }
        return (
            <TextField
                key={key}
                label={label}
                value={placeholderValues[key] ?? ''}
                onChange={e => handlePlaceholderChange(key, e.target.value)}
                fullWidth
            />
        );
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Genera nuovo documento Word</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    {error && <Alert severity="error">{error}</Alert>}

                    <FormControl fullWidth>
                        <InputLabel>Template</InputLabel>
                        <Select value={selectedTemplate} label="Template" onChange={handleTemplateChange}>
                            {templates.map(t => (
                                <MenuItem key={t.id} value={t.id}>{t.title}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth disabled={installations.length === 0}>
                        <InputLabel>Impianto (opzionale)</InputLabel>
                        <Select
                            value={selectedInstallation}
                            label="Impianto (opzionale)"
                            onChange={e => setSelectedInstallation(e.target.value)}
                        >
                            <MenuItem value=""><em>Nessun impianto</em></MenuItem>
                            {installations.map(inst => (
                                <MenuItem key={inst.id} value={inst.id}>
                                    {[inst.brand, inst.model, inst.unique_code].filter(Boolean).join(' – ')}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box>
                        {phoneNumbers.length > 0 && (
                            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {phoneNumbers.map((p, i) => (
                                    <Button
                                        key={i}
                                        size="small"
                                        variant="outlined"
                                        onClick={() => setRecipientPhone(p.number)}
                                    >
                                        {p.number}{p.ref ? ` (${p.ref})` : ''}
                                    </Button>
                                ))}
                            </Box>
                        )}
                        <TextField
                            label="Cellulare destinatario"
                            value={recipientPhone}
                            onChange={e => setRecipientPhone(e.target.value)}
                            fullWidth
                            inputProps={{ maxLength: 10 }}
                            helperText="Numero italiano (es. 3331234567)"
                            error={recipientPhone !== '' && !/^3\d{8,9}$/.test(recipientPhone)}
                        />
                    </Box>

                    <Divider />
                    <Typography variant="subtitle2" color="text.secondary">Campi del documento</Typography>

                    {parsePlaceholders(currentTemplate?.placeholders).map(renderPlaceholderField)}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Annulla</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} /> : null}
                >
                    {loading ? 'Generazione…' : 'Genera e scarica'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default CustomerPdfForm;
